import { useState } from 'react'
import { Check } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { API_CONFIG } from '../config/api'
import SEO from '../components/SEO'
import { buildWebPageSchema } from '../utils/schema'
import { RouteLayout, RouteSection } from '../components/RouteLayout'
import { getWhatsAppUrl } from '../utils/whatsapp'

export default function Contact() {
  const { franchiseData, loading } = useFranchiseData()
  const brandName = franchiseData?.name || 'VAIA World'
  const whatsappUrl = getWhatsAppUrl(
    franchiseData,
    'Hi, I would like to plan a trip with a VAIA World Lifestyle Planner.'
  )

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pax: '',
    dates: '',
    origin: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const params = new URLSearchParams({
        'lead[first_name]': formData.firstName,
        'lead[last_name]': formData.lastName,
        'lead[email]': formData.email,
        'lead[telephone]': formData.phone,
        'lead[message]': formData.message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': formData.pax,
        'lead[dates]': formData.dates,
        'lead[origin]': formData.origin,
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pax: '',
          dates: '',
          origin: '',
          message: '',
        })
      } else {
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again.')
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass =
    'w-full px-3 py-2.5 border border-line rounded-[10px] bg-paper text-ink focus:ring-2 focus:ring-vaia-amber focus:border-transparent outline-none'

  if (loading || !franchiseData) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-line rounded w-64 mx-auto mb-4" />
          <div className="h-4 bg-line rounded w-48 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream">
      <SEO
        title={`Contact | ${brandName}`}
        description="Chat to a Lifestyle Planner — we'll help you plan flights, stays and visa guidance."
        canonical={typeof window !== 'undefined' ? window.location.href : undefined}
        openGraph={{
          type: 'website',
          title: `Contact | ${brandName}`,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        }}
        twitter={{ card: 'summary_large_image' }}
        jsonLd={buildWebPageSchema({
          name: 'Contact',
          description: 'Chat to a Lifestyle Planner — we will help you plan flights, stays and visa guidance.',
          url: typeof window !== 'undefined' ? window.location.href : '',
        })}
      />

      <RouteLayout>
        <RouteSection mark={1} className="hero !pt-[70px]" markTop={70}>
          <div className="eyebrow">Contact</div>
          <h1 className="text-[clamp(32px,5vw,50px)] leading-[1.02] m-0">
            Chat to a<br />Lifestyle Planner.
          </h1>
          <p className="text-lg text-ink-soft max-w-[560px] mt-5 leading-relaxed">
            Tell us where you want to go — we'll take care of flights, stays and visa guidance.
          </p>
          <div className="flex gap-3.5 mt-8 flex-wrap">
            <a
              href={whatsappUrl}
              target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
              rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="btn-primary"
            >
              WhatsApp Us
            </a>
            <a href="#enquire" className="btn-ghost">
              Send a Message
            </a>
          </div>
        </RouteSection>

        <RouteSection mark={2} className="!pt-0">
          <div className="trust-strip" style={{ marginTop: 0 }}>
            {franchiseData.telephone && (
              <div className="trust-item">
                <div className="num">PHONE</div>
                <p>{franchiseData.telephone}</p>
              </div>
            )}
            {franchiseData.email && (
              <div className="trust-item">
                <div className="num">EMAIL</div>
                <p>{franchiseData.email}</p>
              </div>
            )}
            <div className="trust-item">
              <div className="num">HOURS</div>
              <p>Mon – Fri, 09:00 – 17:00</p>
            </div>
            <div className="trust-item">
              <div className="num">BASED</div>
              <p>Pretoria, South Africa</p>
            </div>
          </div>
        </RouteSection>

        <RouteSection mark={3} className="!pt-0 !max-w-[820px]" id="enquire">
          <div className="sec-head">
            <h2>Send a message</h2>
          </div>

          <div className="bg-paper border border-line rounded-[14px] p-6 sm:p-8">
            {submitSuccess ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-[#FBF1DF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-forest" />
                </div>
                <h3 className="text-xl m-0">Message sent</h3>
                <p className="text-ink-soft mt-2 mb-0">
                  A Lifestyle Planner will get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-forest mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-forest mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-forest mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-forest mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pax" className="block text-sm font-medium text-forest mb-1">
                      Number of people *
                    </label>
                    <input
                      type="text"
                      name="pax"
                      id="pax"
                      required
                      value={formData.pax}
                      onChange={handleChange}
                      placeholder="e.g. 2 adults"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="dates" className="block text-sm font-medium text-forest mb-1">
                      Travel dates *
                    </label>
                    <input
                      type="text"
                      name="dates"
                      id="dates"
                      required
                      value={formData.dates}
                      onChange={handleChange}
                      placeholder="e.g. Dec 2026"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="origin" className="block text-sm font-medium text-forest mb-1">
                    Departure city *
                  </label>
                  <input
                    type="text"
                    name="origin"
                    id="origin"
                    required
                    value={formData.origin}
                    onChange={handleChange}
                    placeholder="e.g. Johannesburg"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-forest mb-1">
                    Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Where would you like to go?"
                    className={`${inputClass} resize-none`}
                  />
                </div>

                {submitError && (
                  <div className="bg-[#FBF1DF] border border-vaia-amber rounded-[10px] p-3">
                    <p className="text-vaia-amber-deep text-sm">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </RouteSection>
      </RouteLayout>
    </div>
  )
}
